import { useStudyCreateGroups } from '@/utils/studyCreation/useStudyCreate';
import { useMemo } from 'react';
import {
  type GroupTarget,
  type PlannedAnimalTarget,
  type RegisteredAnimalTarget,
  type TempGroupApiId,
} from '@/model/SamplePlan.model';
import { _notNil } from '@/littledash';
import { generateTempApiId, isTempApiId, isUUID } from '@/utils/ApiId.utils';

export const useSamplePlanGroupData = (): {
  groupTargets: Array<GroupTarget>;
  groupIdMap: Map<string, TempGroupApiId>;
} => {
  const { groups } = useStudyCreateGroups();
  return useMemo<{ groupTargets: Array<GroupTarget>; groupIdMap: Map<string, TempGroupApiId> }>(() => {
    return (groups ?? []).reduce<{
      groupTargets: Array<GroupTarget>;
      groupIdMap: Map<string, TempGroupApiId>;
    }>(
      (acc, group) => {
        if (_notNil(group.temp_id)) {
          const api_id = generateTempApiId('grp', group.temp_id);
          acc.groupIdMap.set(group.temp_id, api_id);
          acc.groupTargets.push({
            api_id,
            name: group.name,
            color: group.color,
            no: group.no,
            max_subjects: group.max_subjects,
          });
        }
        return acc;
      },
      { groupTargets: [], groupIdMap: new Map() }
    );
  }, [groups]);
};
export const useSamplePlanAnimalData = (): {
  planned_animals: Array<PlannedAnimalTarget>;
  registered_animals: Array<RegisteredAnimalTarget>;
} => {
  const { plannedAnimalData } = useStudyCreateGroups();
  return useMemo<{
    planned_animals: Array<PlannedAnimalTarget>;
    registered_animals: Array<RegisteredAnimalTarget>;
  }>(() => {
    switch (plannedAnimalData.type) {
      case 'planned': {
        return {
          registered_animals: [],
          planned_animals: (plannedAnimalData.animals ?? []).reduce<Array<PlannedAnimalTarget>>((acc, animal) => {
            const api_id = isTempApiId('adf', animal.temp_id) ? animal.temp_id : null;
            const study_group_api_id = isUUID(animal.group_temp_id)
              ? generateTempApiId('grp', animal.group_temp_id)
              : isTempApiId('grp', animal.group_temp_id)
                ? animal.group_temp_id
                : null;
            if (_notNil(api_id) && _notNil(study_group_api_id)) {
              acc.push({
                api_id,
                study_group_api_id,
                planned_id: animal.name,
                animal_api_id: null,
              });
            }
            return acc;
          }, []),
        };
      }
      case 'registered': {
        return {
          registered_animals: (plannedAnimalData.animals ?? []).reduce<Array<RegisteredAnimalTarget>>((acc, animal) => {
            const api_id = isTempApiId('aml', animal.temp_id) ? animal.temp_id : null;
            const study_group_api_id = isUUID(animal.group_temp_id)
              ? generateTempApiId('grp', animal.group_temp_id)
              : isTempApiId('grp', animal.group_temp_id)
                ? animal.group_temp_id
                : null;
            if (_notNil(api_id) && _notNil(study_group_api_id)) {
              acc.push({
                api_id,
                study_group_api_id,
                name: animal.name,
              });
            }
            return acc;
          }, []),
          planned_animals: [],
        };
      }
      default: {
        return { registered_animals: [], planned_animals: [] };
      }
    }
  }, [plannedAnimalData]);
};
